/**
 * Dev Diagnostics Page - Admin Only
 * Sprint 10: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { healthAPI, diagnosticsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Activity, 
  Database, 
  Server, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  Users,
  Clock,
  FileText,
  GraduationCap,
  ClipboardCheck,
  Calendar,
  FileSpreadsheet,
  Shield,
  Settings,
} from 'lucide-react';

function ErrorItem({ err, index }) {
  return (
    <div 
      className="p-3 bg-slate-50 rounded-lg border border-slate-200"
      data-testid={`error-item-${index}`}
    >
      <div className="flex items-start justify-between mb-2">
        <Badge variant="destructive" className="text-xs">
          {err.error_type}
        </Badge>
        <span className="text-xs text-slate-500 flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          {new Date(err.timestamp).toLocaleString()}
        </span>
      </div>
      <p className="text-sm text-slate-700 font-mono break-all">
        {err.message}
      </p>
      {err.path && (
        <p className="text-xs text-slate-500 mt-1">
          {err.method} {err.path}
        </p>
      )}
      {err.correlation_id && (
        <p className="text-xs text-slate-400 mt-1">
          Correlation: {err.correlation_id}
        </p>
      )}
    </div>
  );
}

function ErrorsList({ errors }) {
  if (!errors || errors.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
        <p>No errors captured</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-64 overflow-y-auto">
      {errors.map((err, index) => (
        <ErrorItem key={err.id || index} err={err} index={index} />
      ))}
    </div>
  );
}

// Quick access modules with Avida styling
const MODULES = [
  { title: "Survey Readiness", href: "/survey-dashboard", icon: ClipboardCheck, color: "bg-emerald-500", description: "Compliance dashboard & exports" },
  { title: "Clients", href: "/clients", icon: Users, color: "bg-avida-blue", description: "Client intake & management" },
  { title: "Schedule", href: "/schedule", icon: Calendar, color: "bg-purple-500", description: "Visits & scheduling" },
  { title: "EVV Inbox", href: "/evv-exceptions", icon: AlertTriangle, color: "bg-amber-500", description: "EVV exception management" },
  { title: "Sandata Import", href: "/sandata-import", icon: FileSpreadsheet, color: "bg-avida-yellow text-slate-900", description: "Import Sandata exports" },
  { title: "Personnel", href: "/personnel", icon: GraduationCap, color: "bg-indigo-500", description: "Staff & training tracking" },
  { title: "Documents", href: "/documents", icon: FileText, color: "bg-teal-500", description: "Document library" },
  { title: "Users", href: "/users", icon: Settings, color: "bg-slate-600", description: "User management" },
  { title: "Audit Logs", href: "/audit-logs", icon: Shield, color: "bg-rose-500", description: "System activity trail" },
];

function DevDiagnostics() {
  const navigate = useNavigate();
  const { user, isAdmin, canViewAuditLogs } = useAuth();
  const [health, setHealth] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      if (!isAdmin) {
        setLoading(false);
        return;
      }
      try {
        setError(null);
        const healthRes = await healthAPI.check();
        const diagRes = await diagnosticsAPI.get();
        setHealth(healthRes.data);
        setDiagnostics(diagRes.data);
      } catch (err) {
        const message = err.response && err.response.data && err.response.data.detail 
          ? err.response.data.detail 
          : 'Failed to fetch diagnostics';
        setError(message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    }
    fetchData();
  }, [isAdmin]);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const healthRes = await healthAPI.check();
      const diagRes = await diagnosticsAPI.get();
      setHealth(healthRes.data);
      setDiagnostics(diagRes.data);
    } catch (err) {
      const message = err.response && err.response.data && err.response.data.detail 
        ? err.response.data.detail 
        : 'Failed to fetch diagnostics';
      setError(message);
    } finally {
      setRefreshing(false);
    }
  }

  if (!isAdmin) {
    return (
      <Layout title="Access Denied">
        <div className="flex items-center justify-center py-12" data-testid="access-denied">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-600 mb-4">
                This page is only accessible to Administrators.
              </p>
              {canViewAuditLogs && (
                <Button onClick={() => navigate('/audit-logs')} variant="outline" className="mr-2">
                  View Audit Logs
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2 text-slate-600">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading diagnostics...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const appHealthy = health && health.app_status === 'healthy';
  const dbConnected = health && health.db_status === 'connected';
  const envValue = (diagnostics && diagnostics.environment) || (health && health.environment) || 'N/A';
  const appStatus = (health && health.app_status) || 'Unknown';
  const dbStatus = (health && health.db_status) || 'Unknown';
  const migrationsCount = (diagnostics && diagnostics.migrations_applied) || 0;
  const usersCount = (diagnostics && diagnostics.users_count) || 0;
  const rolesCount = (diagnostics && diagnostics.roles_count) || 0;
  const recentErrors = (diagnostics && diagnostics.recent_errors) || [];

  return (
    <Layout title="Dashboard">
      <div data-testid="dev-diagnostics-page" className="space-y-6">
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Access Modules */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">Quick Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULES.map((mod) => (
              <Card 
                key={mod.href}
                className="cursor-pointer hover:shadow-md hover:border-avida-blue-200 transition-all duration-200 group"
                onClick={() => navigate(mod.href)}
                data-testid={`nav-${mod.href.replace('/', '')}`}
              >
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${mod.color} text-white shadow-sm`}>
                      <mod.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 group-hover:text-avida-blue transition-colors">{mod.title}</p>
                      <p className="text-sm text-slate-500">{mod.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* System Status */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900">System Status</h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${appHealthy ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    {appHealthy ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Application</p>
                    <p className={`font-semibold ${appHealthy ? 'text-emerald-600' : 'text-red-600'}`}>
                      {appStatus}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${dbConnected ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <Database className={`h-5 w-5 ${dbConnected ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Database</p>
                    <p className={`font-semibold ${dbConnected ? 'text-emerald-600' : 'text-red-600'}`}>
                      {dbStatus}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-full bg-avida-blue-100">
                    <Users className="h-5 w-5 text-avida-blue" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Users</p>
                    <p className="font-semibold text-slate-900">{usersCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-full bg-purple-100">
                    <Server className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Environment</p>
                    <p className="font-semibold text-slate-900">{envValue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Errors */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Recent Errors</CardTitle>
              <Badge variant="outline" className={recentErrors.length > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                {recentErrors.length} {recentErrors.length === 1 ? 'error' : 'errors'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ErrorsList errors={recentErrors} />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default DevDiagnostics;
